// The softphone's SIP session, hoisted OUT of the page component.
//
// WHY THIS EXISTS: useSip owns a sip.js UserAgent, and its cleanup calls
// registerer.unregister() + ua.stop(). While the hook lived inside
// VoiceSoftphone.tsx, that cleanup fired on every route change — so navigating
// from the softphone to Real-time Call Logs (or anywhere else) tore down the
// UserAgent and HUNG UP THE LIVE CALL. Agents saw calls "randomly" drop after
// 10-20 seconds; that was just how long it took them to click another tab.
//
// A call-centre softphone has to survive navigation by definition: the whole
// point of a screen pop is that the agent looks up the customer, reads their
// history and takes notes WHILE talking. docs/live-agent-softphone.md §15 lists
// "Softphone always-on vs per-conversation" as an open question — for a live
// desk the answer is always-on, so the session is mounted once for the whole
// authenticated app and every page reads the same one.
//
// Registration state is intentionally NOT reset between pages: the agent
// registers once at login and stays routable until they sign out.

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { opsFetch, readCsrfCookie } from "../../api";
import { CHOSEN_KEY, HEARTBEAT_MS, PAUSE_REASON_KEY, reassertBody } from "./desk-heartbeat-client";
import { PBX } from "./pbx-config";
import { setReloadBusy } from "../../lib/build-freshness";
import { useSip, type SipApi, type SipConfig } from "./useSip";
import { judgeSessionOwner, SESSION_CHANGED_MESSAGE } from "./softphone-owner";

/** How often a registered tab re-asks who is signed in, as the backstop for
 *  changes no event reports (another port in dev, a session revoked by an
 *  admin). Storage and focus events catch the common case at once. */
const OWNER_CHECK_MS = 20_000;

// The desk's caller ID / teams / numbers live beside the registration — why:
// desk-info.ts.
import { deskInfoFrom, type DeskInfo, type SoftphoneCredentials } from "./desk-info";
export { deskInfoFrom, type DeskInfo, type SoftphoneCredentials };

interface SipContextValue extends SipApi {
  /** The config the session is running on (null = not registered yet). */
  config: SipConfig | null;
  /** Where the registered phone is plugged in; null until the server says. */
  desk: DeskInfo | null;
  /** Register (or re-register) with these credentials. `desk` comes with a
   *  server-issued credential; the manual form has none to give. */
  connect: (config: SipConfig, desk?: DeskInfo | null) => void;
  /** Unregister and forget the saved login. */
  disconnect: () => void;
  /**
   * A registration is being restored after a page reload. Callers that would
   * otherwise start their own must wait, or the two race and each replaces the
   * other's UserAgent.
   */
  restoring: boolean;
  /** Why the phone was taken off without the agent asking, if it was — the
   *  desk states it on the signed-out face. Cleared by the next connect(). */
  signedOutReason: string | null;
}

const Ctx = createContext<SipContextValue | null>(null);

export const SOFTPHONE_REG_KEY = "chloe-ops:softphone-reg";

/**
 * "This tab had the phone switched on."
 *
 * A browser refresh destroys the React tree, so `config` returns to null and the
 * WebSocket closes — and a WSS contact is bound to its socket, so Asterisk drops
 * it at once. The agent is then unregistered with nothing on screen saying so:
 * their extension reads Unavailable, PJSIP_DIAL_CONTACTS returns empty, and the
 * queue rings straight past them. Navigation was already survivable (that is why
 * SipProvider sits above the router); reload was not.
 *
 * sessionStorage, deliberately, on both counts:
 *
 *   - it SURVIVES a reload, which is the case being fixed;
 *   - it is PER TAB and dies with the tab, so a new window does not inherit a
 *     phone, and closing the tab is still a clean sign-off.
 *
 * localStorage would have made every future tab auto-register — on a shared
 * machine, for whoever registered last. That is the same identity leak the
 * SOFTPHONE_REG_KEY comment below describes, and it is why the restore re-fetches
 * credentials from the server (which resolves the extension from the SESSION)
 * instead of replaying a stored password.
 */
const SOFTPHONE_LIVE_KEY = "chloe-ops:softphone-live";

function markLive(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(SOFTPHONE_LIVE_KEY, "1");
    else sessionStorage.removeItem(SOFTPHONE_LIVE_KEY);
  } catch { /* private mode — restore is a convenience, never a requirement */ }
}

function wasLive(): boolean {
  try { return sessionStorage.getItem(SOFTPHONE_LIVE_KEY) === "1"; } catch { return false; }
}

export function readSavedReg(): { ext?: string; pw?: string } | null {
  try {
    return JSON.parse(localStorage.getItem(SOFTPHONE_REG_KEY) || "null");
  } catch {
    return null;
  }
}

/**
 * Forget the stored SIP credential.
 *
 * This key is per-BROWSER, not per-user, so it must be cleared whenever the
 * person using the browser might change — on login and on logout. Without that,
 * the next user to sign in on a shared machine inherits the previous agent's
 * extension: two people appeared as the same extension, rang on the same calls,
 * and every one of those calls was attributed to the wrong agent.
 */
export function clearSavedReg(): void {
  try { localStorage.removeItem(SOFTPHONE_REG_KEY); } catch { /* ignore */ }
  // Same reasoning, same moments: a login or logout means the person may have
  // changed, so the tab must not silently re-register as the previous one.
  markLive(false);
}

export function SipProvider({ children }: { children: ReactNode }) {
  // Held here, so it is created once per session rather than once per visit to
  // the softphone page. A new object identity would re-run useSip's connect
  // effect — which is exactly the teardown this provider exists to prevent.
  const [config, setConfig] = useState<SipConfig | null>(null);
  const [desk, setDesk] = useState<DeskInfo | null>(null);
  const sip = useSip(config);

  // RESTORES, NEVER INITIATES. The flag is only ever set by a real connect(), so
  // a user who has not registered — a supervisor, an admin, anyone without an
  // extension — opens /ops and stays a silent observer. Auto-registering every
  // user who *owns* an extension would have been the simpler rule and the wrong
  // one: it turns opening the dashboard into going on shift, and a phone starts
  // ringing at someone who never picked it up.
  const [restoring, setRestoring] = useState(wasLive);

  useEffect(() => {
    if (!restoring) return;
    let cancelled = false;
    void (async () => {
      try {
        // Re-issued by the server, not replayed from storage: it resolves the
        // extension from the session, so a different user refreshing this tab
        // gets their own line or nothing at all — never the last agent's.
        const c = await opsFetch<SoftphoneCredentials>(
          "/ops/api/voice/softphone/credentials",
        );
        if (cancelled) return;
        setDesk(deskInfoFrom(c));
        setConfig({
          ...PBX,
          ...(c.domain ? { domain: c.domain } : {}),
          ...(c.wss ? { wss: c.wss } : {}),
          ext: c.ext,
          password: c.password,
        });
      } catch {
        // no_extension, an expired session, an older deployment without the
        // endpoint. Drop the flag so this does not retry on every reload, and
        // leave the phone off — the Softphone page reports the reason properly
        // when the agent goes looking for it. Failing quiet here is right: this
        // path runs on pages that have nothing to do with the phone.
        markLive(false);
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restoring]);
  // Moving the tab onto a new build is a reload, and a reload hangs up the
  // phone. Hold it off while a call is ringing or live (lib/build-freshness.ts).
  const onCall = sip.call != null || sip.incoming != null;
  useEffect(() => setReloadBusy(onCall), [onCall]);

  // ── THE PHONE BELONGS TO WHOEVER SWITCHED IT ON ───────────────────────────
  // One session cookie serves every tab, so signing in as someone else in
  // another tab (or signing out there) changes the session under this tab's
  // desk. The SIP registration does not notice — it answers to Asterisk, not
  // the cookie — so the agent's extension kept ringing and could be answered
  // while the browser was signed in as an admin (TC-A01.4). Record the owner
  // when the phone comes on, and re-ask the server whenever that may have
  // changed: another tab wrote auth state (storage), the agent came back to
  // this tab (focus / visibility), or on a slow timer as a backstop.
  const ownerRef = useRef<string | null>(null);
  const [dropPending, setDropPending] = useState(false);
  const [signedOutReason, setSignedOutReason] = useState<string | null>(null);

  useEffect(() => {
    if (!config) { ownerRef.current = null; return; }
    let cancelled = false;
    let inFlight = false;
    const check = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        // /api/status answers `me: null` rather than 401 when signed out, so
        // this never trips api.ts's global redirect to the login page.
        const s = await opsFetch<{ me: { id: string } | null }>("/ops/api/status");
        if (cancelled) return;
        const verdict = judgeSessionOwner(ownerRef.current, s.me?.id ?? null);
        if (verdict === "claim") ownerRef.current = s.me!.id;
        else if (verdict === "drop") setDropPending(true);
      } catch {
        // Unreachable server is not a changed identity — keep the phone.
      } finally {
        inFlight = false;
      }
    };
    void check();
    const onStorage = () => void check();
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => void check(), OWNER_CHECK_MS);
    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [config]);

  // ── THE DESK HEARTBEAT, WHEREVER THE PHONE IS ON ─────────────────────────
  // The server signs out a desk that stops checking in for three minutes
  // (server/ops/desk-heartbeat.ts). This used to run only on the Agent desktop
  // page, so an Available agent who moved to Conversations — phone still
  // registered, calls still answerable from the CallBar — went silent and was
  // signed out, and stopped receiving queue calls (seen on dev, 21 Oct 2026).
  // It lives here now, beside the registration it vouches for.
  //
  // If the server reports it signed this desk out ITSELF (it stopped hearing
  // us — a slow network, a long-throttled tab), the desk is plainly alive, so
  // re-announce the agent's own choice as the desk saved it. A supervisor's
  // sign-out or the agent's own carries no such marker and stands.
  const registered = sip.registered;
  useEffect(() => {
    if (!config || !registered) return;
    let stop = false;
    const beat = () => {
      void opsFetch<{ deskSystemSignedOut?: boolean }>("/ops/api/voice/softphone/presence?desk=1")
        .then((d) => {
          if (stop || !d?.deskSystemSignedOut) return;
          let chosen: string | null = null;
          let reason: string | null = null;
          try { chosen = localStorage.getItem(CHOSEN_KEY); reason = localStorage.getItem(PAUSE_REASON_KEY); } catch { /* private mode */ }
          void opsFetch("/ops/api/voice/softphone/presence", { method: "POST", json: reassertBody(chosen, reason) })
            .catch(() => { /* the next beat tries again */ });
        })
        .catch(() => { /* advisory: a missed beat is what the three-minute window is for */ });
    };
    beat();
    const timer = window.setInterval(beat, HEARTBEAT_MS);
    return () => { stop = true; window.clearInterval(timer); };
  }, [config, registered]);

  // CLOSING THE TAB SIGNS THE DESK OUT, from any page. This lived on the Agent
  // desktop page, so closing the tab from Conversations told the server nothing.
  // Only while the phone is on: without a phone there is no line to sign out.
  // keepalive is the one fetch mode that outlives its document.
  useEffect(() => {
    if (!config) return;
    const onHide = () => {
      const csrf = readCsrfCookie() ?? undefined;
      try {
        void fetch("/ops/api/voice/softphone/presence", {
          method: "POST", credentials: "include", keepalive: true,
          headers: { "Content-Type": "application/json", ...(csrf ? { "X-Ops-CSRF": decodeURIComponent(csrf) } : {}) },
          body: JSON.stringify({ presence: "offline" }),
        });
      } catch { /* page is going away */ }
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [config]);

  // Take the phone off — but never cut a connected customer mid-sentence. A
  // call already in progress is allowed to finish (the PBX ends it, or the
  // agent does); the registration goes the moment it does. A call that is
  // only RINGING is not protected: it must not be offered to a desk that no
  // longer belongs to anyone signed in.
  const liveCall = sip.call != null;
  useEffect(() => {
    if (!dropPending || liveCall) return;
    setDropPending(false);
    try { localStorage.removeItem(SOFTPHONE_REG_KEY); } catch { /* ignore */ }
    markLive(false);
    ownerRef.current = null;
    setSignedOutReason(SESSION_CHANGED_MESSAGE);
    setDesk(null);
    setConfig(null);
  }, [dropPending, liveCall]);

  const value = useMemo<SipContextValue>(() => ({
    ...sip,
    config,
    desk,
    restoring,
    signedOutReason,
    connect: (c: SipConfig, d?: DeskInfo | null) => {
      try { localStorage.setItem(SOFTPHONE_REG_KEY, JSON.stringify({ ext: c.ext, pw: c.password })); } catch { /* ignore */ }
      markLive(true);
      setSignedOutReason(null);
      setDesk(d ?? null);
      setConfig(c);
    },
    disconnect: () => {
      try { localStorage.removeItem(SOFTPHONE_REG_KEY); } catch { /* ignore */ }
      // An explicit sign-off must not come back after a refresh.
      markLive(false);
      setDesk(null);
      setConfig(null);
    },
  }), [sip, config, desk, restoring, signedOutReason]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSoftphone(): SipContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSoftphone must be used inside <SipProvider>");
  return v;
}
